'use client';

import { useActiveProjectId } from '../contexts/ActiveEntitiesContext/ActiveProjectIdContext';
import { useCurrentPageType } from '../routing';
import { useGetAiAssistantStatusQuery } from './gql/generated/GetAiAssistantStatus';

/**
 * Page types where the AI panel is available.
 * Excludes received-share review pages, settings, search, etc.
 */
const AI_PANEL_ALLOWED_PAGE_TYPES = [
  'folder',
  'finder',
  'folderViewer',
  'collection',
  'collectionViewer',
  'trash',
  'shareBuilder',
  'shareBuilderViewer',
  'allShares',
] as const;

/**
 * Hook that determines if the AI panel feature is available on the current page.
 * Checks both the feature flag and page type.
 */
export const useIsAIPanelAvailable = () => {
  const projectId = useActiveProjectId();
  const pageType = useCurrentPageType();
  const isAllowedPage = AI_PANEL_ALLOWED_PAGE_TYPES.includes(
    pageType as (typeof AI_PANEL_ALLOWED_PAGE_TYPES)[number]
  );

  const { data: aiAssistantStatus } = useGetAiAssistantStatusQuery({
    variables: {
      projectId: projectId!,
    },
    skip: !projectId || !isAllowedPage,
  });

  /**
   * Note: the ai assistant status query checks against the flag on the server side as well, but leaving in the separate
   * feature flag check in case we want to debug by overriding the flag to false locally.
   */
  const isArchonEnabled =
    aiAssistantStatus?.account?.project?.aiAssistant?.status === 'ENABLED';

  return isArchonEnabled && isAllowedPage;
};
